<template>
  <div class="message-container pa-1" :class="messageContainerClass">
    <div class="message-wrapper d-flex align-end">
      <!-- Avatar (only for messages not from current user) -->
      <div v-if="!item.isCurrentUser && showAvatar">
        <user-avatar :user="item.user" small />
      </div>

      <!-- Message bubble -->
      <div class="bubble-container d-flex flex-column">
        <!-- Header (title) for messages not from current user -->
        <div v-if="!item.isCurrentUser && item.title" class="caption pl-3 mb-1 font-weight-medium">
          {{ item.title }}
        </div>

        <!-- Message bubble -->
        <div class="message-bubble-wrapper d-flex align-stretch">
          <div class="message-bubble" :class="[bubbleClasses, { 'timeline-long-press-active': isPressed }]"
            :style="{ backgroundColor: backgroundColor, color: foregroundColor }"
            @mousedown="interactionHandlers.onPressStart" @mouseup="interactionHandlers.onPressEnd"
            @mouseleave="interactionHandlers.onPressCancel" @touchstart="interactionHandlers.onPressStart"
            @touchend="interactionHandlers.onPressEnd" @touchcancel="interactionHandlers.onPressCancel">
            <div class="message-content pa-3 d-flex flex-column">

              <!-- Subtitle -->
              <div v-if="item.subtitle" class="subtitle-2">
                {{ item.subtitle }}
              </div>

              <!-- Message content -->
              <div v-if="item.message" class="message-text body-1 text-break text-pre-line">
                <div v-if="shouldTruncateMessage">
                  <div v-if="!expanded">
                    {{ truncatedMessage }}
                    <v-btn text x-small class="text-decoration-underline text-lowercase pa-1 ml-1"
                      :style="{ color: foregroundColor }" @click="expanded = true">
                      więcej
                    </v-btn>
                  </div>
                  <div v-else>
                    {{ item.message }}
                    <v-btn text x-small class="text-decoration-underline text-lowercase pa-1 ml-1"
                      :style="{ color: foregroundColor }" @click="expanded = false">
                      mniej
                    </v-btn>
                  </div>
                </div>
                <div v-else>
                  {{ item.message }}
                </div>
              </div>

              <!-- Details -->
              <div v-if="item.details" class="message-details body-1 text-break text-pre-line">
                {{ item.details }}
              </div>

              <!-- Attachments -->
              <div v-if="item.attachments && item.attachments.length" class="mt-2 d-flex flex-wrap">
                <v-chip v-for="(attachment, index) in item.attachments" :key="index" class="ma-0 mr-1 mb-1" x-small
                  outlined @click="openAttachment(attachment)">
                  <v-icon x-small class="mr-1">mdi-paperclip</v-icon>
                  {{ attachment.name }}
                </v-chip>
              </div>

              <!-- Chips -->
              <div v-if="item.chips && item.chips.length" class="mt-2 d-flex flex-wrap">
                <v-chip v-for="(chip, index) in item.chips" :key="index" class="ma-0 mr-1 mb-1" x-small outlined
                  :color="foregroundColor">
                  <v-icon x-small class="mr-1">{{ chip.icon }}</v-icon>
                  {{ chip.name }}
                </v-chip>
              </div>
            </div>
          </div>

          <!-- Error indicator -->
          <div v-if="item.status === MessageStatus.ERROR" class="error-indicator d-flex align-center pr-1">
            <v-icon color="error" size="20">mdi-alert-circle</v-icon>
          </div>
          <!-- Draft indicator -->
          <div v-if="item.status === MessageStatus.DRAFT" class="draft-indicator d-flex align-center pr-1">
            <v-icon color="orange" size="20">mdi-clock-outline</v-icon>
          </div>
          <!-- Success indicator for updated drafts -->
          <div v-if="item.status === MessageStatus.RECEIVED && item.id && item.id.startsWith('draft-')"
            class="success-indicator d-flex align-center pr-1">
            <v-icon color="success" size="20">mdi-check-circle</v-icon>
          </div>
          <!-- Time and status -->
          <div class="message-time caption mx-1">
            {{ formatTime(item.date || item.createdAt) }}
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import UserAvatar from '@/components/UserAvatar.vue'
import { timelineEditMixin } from '@/mixins/timelineEdit.js'
import { MessageStatus } from '@/util/timeline'

export default {
  name: 'MessageItemTimeline',

  components: {
    UserAvatar
  },

  mixins: [timelineEditMixin],

  props: {
    item: {
      type: Object,
      required: true
    },
    readOnly: {
      type: Boolean,
      default: false
    },
    showAvatar: {
      type: Boolean,
      default: true
    },
    // New props for configuring edit methods
    enableLongPress: {
      type: Boolean,
      default: true
    }
  },

  data() {
    return {
      expanded: false,
      isPressed: false,
      MessageStatus
    }
  },

  computed: {
    editMethodsConfig() {
      return {
        longPress: this.enableLongPress && this.canEdit
      }
    },

    interactionHandlers() {
      return this.getSafeInteractionHandlers(
        this.item,
        this.handleEdit,
        this.readOnly
      );
    },

    important() {
      return this.item.important || false
    },

    canEdit() {
      // Standard edit logic - check only canEdit and source
      return (this.item.canEdit !== false) && !!this.item.source;
    },

    editTooltip() {
      return this.item.editTooltip || 'Edytuj'
    },

    truncatedMessage() {
      if (this.item.message && this.item.message.length > 200) {
        return this.item.message.substring(0, 200) + '...'
      }
      if (this.item.message && this.item.message.split('\n').length > 3) {
        return this.item.message.split('\n').slice(0, 3).join('\n') + '...'
      }
      return this.item.message
    },

    shouldTruncateMessage() {
      if (!this.item.message) return false
      return this.item.message.length > 200 || this.item.message.split('\n').length > 3
    },

    messageContainerClass() {
      return {
        'current-user': this.item.isCurrentUser,
        'other-user': !this.item.isCurrentUser,
        'important': this.important
      }
    },

    bubbleClasses() {
      return {
        'bubble-current-user': this.item.isCurrentUser,
        'bubble-other-user': !this.item.isCurrentUser,
        'bubble-important': this.important,
        'bubble-error': this.item.status === MessageStatus.ERROR,
        'bubble-draft': this.item.status === MessageStatus.DRAFT
      }
    },

    foregroundColor() {
      const currentTheme = this.$vuetify.theme.themes[this.$vuetify.theme.dark ? 'dark' : 'light']

      if (this.important) return currentTheme.messageImportantText
      if (this.item.isCurrentUser) return currentTheme.messageCurrentUserText
      return currentTheme.messageOtherUserText
    },

    backgroundColor() {
      const currentTheme = this.$vuetify.theme.themes[this.$vuetify.theme.dark ? 'dark' : 'light']

      if (this.important) return currentTheme.messageImportantBg
      if (this.item.isCurrentUser) return currentTheme.messageCurrentUserBg
      return currentTheme.messageOtherUserBg
    }
  },

  mounted() {
    // Set edit methods for mixin - required by timelineEditMixin
    this.editMethods = this.editMethodsConfig;
  },

  methods: {
    // Override mixin method - fix for missing editMethod
    canEditItem(item) {
      // Standard logic without checking editMethod
      const result = item.canEdit !== false && !this.readOnly;
      return result;
    },

    handleEdit() {
      // Standard editing - emit event to parent
      this.$emit('edit', {
        item: this.item,
        canDelete: this.item.canDelete === true
      });
    },

    openAttachment(attachment) {
      this.$emit('open-attachment', attachment)
    },

    formatTime(dateString) {
      if (!dateString) return ''

      const date = new Date(dateString)
      return date.toLocaleTimeString([], {
        hour: '2-digit',
        minute: '2-digit'
      })
    }
  }
}
</script>

<style scoped>
.message-container {
  width: 100%;
}

/* Current user styling */
.current-user .message-wrapper {
  flex-direction: row-reverse;
  padding-left: 70px;
  padding-right: 8px;
}

/* Other user styling */
.other-user .message-wrapper {
  padding-right: 70px;
  padding-left: 0;
}

.bubble-container {
  width: 100%;
}

.current-user .message-bubble-wrapper {
  flex-direction: row-reverse;
}

.message-bubble {
  border-radius: 16px;
  max-width: 100%;
  word-wrap: break-word;
  position: relative;
  cursor: pointer;
  user-select: none;
}

.message-time {
  flex-shrink: 0;
  text-align: center;
  display: flex;
  justify-content: center;
  align-items: center;
  align-self: stretch;
}

/* Draft message styling */
.bubble-draft {
  opacity: 0.7;
  border: 1px dashed #ff9800;
}

/* Responsive design */
@media (max-width: 600px) {
  .current-user .message-wrapper {
    padding-left: 40px;
  }

  .other-user .message-wrapper {
    padding-right: 40px;
  }
}
</style>
